// src/features/tasks/components/TaskDetailsPage/RotationOptionsEditor.jsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Chip,
  Stack,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MAX_OPTIONS = 20;
const MAX_OPTION_LENGTH = 100;

/**
 * Editor for a rotating value on a task-equipment link: a label plus an
 * ordered list of options that will rotate by one step each time the link
 * is checked off and the task is completed (e.g. "Test location": Top,
 * Middle, Bottom).
 */
export default function RotationOptionsEditor({
  enabled,
  onEnabledChange,
  rotationLabel,
  onRotationLabelChange,
  rotationOptions,
  onRotationOptionsChange,
  disabled = false,
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const [draftOption, setDraftOption] = useState('');

  const handleAddOption = () => {
    const value = draftOption.trim();
    if (!value || rotationOptions.includes(value) || rotationOptions.length >= MAX_OPTIONS) return;
    onRotationOptionsChange([...rotationOptions, value.slice(0, MAX_OPTION_LENGTH)]);
    setDraftOption('');
  };

  const handleRemoveOption = (index) => {
    onRotationOptionsChange(rotationOptions.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={t('tasks:equipment.enable_rotation', { defaultValue: 'Rotating value (e.g. test location)' })}
      />

      {enabled && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('tasks:equipment.rotation_hint', {
              defaultValue: 'This value advances to the next option only when this equipment is checked off and the task is completed.',
            })}
          </Typography>

          <TextField
            label={t('tasks:equipment.rotation_label', { defaultValue: 'Label' })}
            value={rotationLabel}
            onChange={(e) => onRotationLabelChange(e.target.value)}
            placeholder={t('tasks:equipment.rotation_label_placeholder', { defaultValue: 'e.g. Test location' })}
            size="small"
            disabled={disabled}
            inputProps={{ maxLength: 100 }}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('tasks:equipment.rotation_options', { defaultValue: 'Options (in order)' })}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {rotationOptions.map((option, index) => (
                <Chip
                  key={`${option}-${index}`}
                  label={`${index + 1}. ${option}`}
                  size="small"
                  onDelete={disabled ? undefined : () => handleRemoveOption(index)}
                />
              ))}
              {rotationOptions.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('tasks:equipment.no_rotation_options', { defaultValue: 'No options added yet' })}
                </Typography>
              )}
            </Stack>
            <Box display="flex" gap={1}>
              <TextField
                value={draftOption}
                onChange={(e) => setDraftOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
                placeholder={t('tasks:equipment.add_option', { defaultValue: 'Add option' })}
                size="small"
                fullWidth
                disabled={disabled || rotationOptions.length >= MAX_OPTIONS}
                inputProps={{ maxLength: MAX_OPTION_LENGTH }}
              />
              <IconButton
                onClick={handleAddOption}
                disabled={disabled || !draftOption.trim() || rotationOptions.length >= MAX_OPTIONS}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
